/// <amd-module name="Feed/Legacy/Protocols/Components/PackageProvenance" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Icon } from "VSSUI/Icon";
import { ago } from "VSSUI/Utilities/Date";

import { Build } from "Build/Client/Contracts/Build";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { PackageVersionProvenance, Provenance } from "Feed/Common/Contracts/Feed";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { InternalLink } from "Feed/Legacy/Components/InternalLink";
import { IFeedProvenanceDataService } from "Feed/Legacy/DataServices/ProvenanceDataService";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { getFullyQualifiedFeedId } from "Feed/Legacy/Helpers/FeedNameResolver";
import { PackageAttribute } from "Feed/Legacy/Protocols/Components/PackageAttribute";
import {
    IFeedProvenanceLinkService,
    IProvenanceLinkData,
    IProvenanceLinks
} from "Feed/Legacy/Protocols/Components/ProvenanceLinkService";
import * as Utils_Html from "Feed/Legacy/Utils/Html";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import { CiConstants, ProvenanceSource } from "Feed/PackageDetails/Constants/Constants";
import * as PackageResources from "Feed/PackageDetails/Resources";

export interface IProvenanceProps {
    pageContext: IVssPageContext;
    feed: Feed;
    packageId: string;
    versionId: string;
    publishDate: Date;
    isProvenanceEnabled: boolean;
    isProvenanceSupported: boolean;
}

export interface IProvenanceState {
    provenance: Provenance;
    build: Build;
    links: IProvenanceLinks;
    repoIcon: string;
}

export class PackageProvenance extends React.Component<IProvenanceProps, IProvenanceState> {
    private _mounted: boolean;

    constructor(props: IProvenanceProps) {
        super(props);
        this.state = { provenance: null } as IProvenanceState;
        this._mounted = false;
    }

    public componentDidMount(): void {
        this._mounted = true;
        this.initialize();
    }

    public componentWillUnmount(): void {
        this._mounted = false;
    }

    public render(): JSX.Element {
        return (
            <div className="package-provenance">
                <PackageAttribute title={PackageResources.PackageAttributeTitle_Provenance}>
                    <div className="package-group-spacer" />
                    {!this.showBuild() &&
                        !this.showRelease() &&
                        !this.showExternal() &&
                        this.renderGenericPublisherSection()}
                    {this.showBuild() && this.renderBuildSection()}
                    {this.showRelease() && this.renderReleaseSection()}
                    {this.showExternal() && this.renderExternalSection()}
                    {this.showCode() && (
                        <div>
                            <div className="package-group-spacer" />
                            {this.renderCodeSection()}
                        </div>
                    )}
                </PackageAttribute>
            </div>
        );
    }

    private async initialize(): Promise<void> {
        if (this.props.isProvenanceEnabled && this.props.isProvenanceSupported) {
            const provenanceDataService = this.props.pageContext.getService<IFeedProvenanceDataService>(
                "IFeedProvenanceDataService"
            );
            const provenanceLinkService = this.props.pageContext.getService<IFeedProvenanceLinkService>(
                "IFeedProvenanceLinkService"
            );
            const feedId = getFullyQualifiedFeedId(this.props.feed);
            let provenance: Provenance = null;
            let links: IProvenanceLinks = null;
            let repoIcon: string = null;

            try {
                const projectId = this.props.feed.project ? this.props.feed.project.id : null;
                const pvp: PackageVersionProvenance = await provenanceDataService.getPackageVersionProvenance(
                    projectId,
                    feedId,
                    this.props.packageId,
                    this.props.versionId
                );
                provenance = pvp.provenance;
                links = await provenanceLinkService.getLinksAsync(provenance);
                repoIcon = this.getRepoIcon(links.repositoryType);
            } catch (err) {
                if (err != null && err.status === 404) {
                    // 404s fetching provenance or links are expected
                } else {
                    throw err;
                }
            } finally {
                if (this._mounted === true) {
                    this.setState({ provenance, links, repoIcon });

                    if (this.showBuild()) {
                        CustomerIntelligenceHelper.publishEvent(
                            this.props.pageContext,
                            CiConstants.ProvenanceShowBuild,
                            {
                                feedId: this.props.feed.id
                            }
                        );
                    } else if (this.showRelease()) {
                        CustomerIntelligenceHelper.publishEvent(
                            this.props.pageContext,
                            CiConstants.ProvenanceShowRelease,
                            {
                                feedId: this.props.feed.id
                            }
                        );
                    } else if (this.showExternal()) {
                        CustomerIntelligenceHelper.publishEvent(
                            this.props.pageContext,
                            CiConstants.ProvenanceShowExternal,
                            {
                                feedId: this.props.feed.id
                            }
                        );
                    }
                }
            }
        }
    }

    private renderGenericPublisherSection(): JSX.Element {
        return (
            <section aria-label={PackageResources.Publisher}>
                <div className="package-group-header" role="heading" aria-level={3}>
                    {PackageResources.Publisher}
                </div>
                {this.canShowProvenance() && (
                    <div className="package-attribute-item">
                        <span className="bowtie-icon bowtie-user package-attribute-item-icon" />
                        {this.state.provenance.data && (
                            <span className="user-name" dangerouslySetInnerHTML={this.getProvenanceUserDisplayName()} />
                        )}
                        {this.state.provenance.userAgent && (
                            <span>
                                {Utils_String.format(
                                    PackageResources.PackageProvenance_UsingUserAgent,
                                    this.state.provenance.userAgent
                                )}
                            </span>
                        )}
                    </div>
                )}
                {this.renderPublishDate()}
            </section>
        );
    }

    private getProvenanceUserDisplayName() {
        return {
            __html:
                this.state.provenance.data["Common.IdentityDisplayName"] ||
                PackageResources.PackageProvenance_UnknownUser
        };
    }

    private renderPublishDate(): JSX.Element {
        return (
            <div className="package-attribute-item">
                <span className="bowtie-icon bowtie-status-waiting package-attribute-item-icon" />
                <span>
                    {ago(this.props.publishDate) +
                        " (" +
                        this.props.publishDate.toLocaleDateString() +
                        " " +
                        this.props.publishDate.toLocaleTimeString() +
                        ")"}
                </span>
            </div>
        );
    }

    private renderBuildSection(): JSX.Element {
        return (
            <section aria-label={PackageResources.Build}>
                <div className="package-group-header" role="heading" aria-level={3}>
                    {PackageResources.Build}
                </div>
                <div className="package-attribute-item">
                    {this.hasLink(this.state.links.buildDefinitionLink) && (
                        <span>
                            <span className="bowtie-icon bowtie-build package-attribute-item-icon" />
                            {this.renderLink(
                                this.state.links.buildDefinitionLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceBuildDefinition,
                                    this.state.links.buildDefinitionLink.text
                                ),
                                CiConstants.ProvenanceBuildDefinitionLinkClicked
                            )}
                        </span>
                    )}
                    {this.hasLink(this.state.links.buildLink) && this.hasLink(this.state.links.buildDefinitionLink) && (
                        <span className="separator">/</span>
                    )}
                    {this.hasLink(this.state.links.buildLink) &&
                        this.renderLink(
                            this.state.links.buildLink,
                            Utils_String.format(
                                PackageResources.AriaLabel_ProvenanceBuild,
                                this.state.links.buildLink.text
                            ),
                            CiConstants.ProvenanceBuildLinkClicked
                        )}
                </div>
                {this.renderPublishDate()}
            </section>
        );
    }

    private renderCodeSection(): JSX.Element {
        return (
            <section aria-label={PackageResources.Code}>
                <div className="package-group-header" role="heading" aria-level={3}>
                    {PackageResources.Code}
                </div>
                <div className="package-attribute-item">
                    {this.hasLink(this.state.links.commitLink) && (
                        <span>
                            <Icon iconName="BranchCommit" className="package-attribute-item-icon" />
                            {this.renderLink(
                                this.state.links.commitLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceCodeCommit,
                                    this.state.links.commitLink.text
                                ),
                                CiConstants.ProvenanceCommitLinkClicked
                            )}
                        </span>
                    )}
                    {this.hasLink(this.state.links.commitLink) && this.hasLink(this.state.links.branchLink) && (
                        <span className="separator">{PackageResources.In}</span>
                    )}
                    {this.hasLink(this.state.links.branchLink) && (
                        <span>
                            <Icon iconName="BranchFork2" className="package-attribute-item-inline-icon" />
                            {this.renderLink(
                                this.state.links.branchLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceCodeBranch,
                                    this.state.links.branchLink.text
                                ),
                                CiConstants.ProvenanceBranchLinkClicked
                            )}
                        </span>
                    )}
                </div>
                {this.hasLink(this.state.links.repositoryLink) && (
                    <div className="package-attribute-item">
                        <span>
                            <Icon iconName={this.state.repoIcon} className="package-attribute-item-icon" />
                            {this.renderLink(
                                this.state.links.repositoryLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceCodeRepository,
                                    this.state.links.repositoryLink.text
                                ),
                                CiConstants.ProvenanceRepositoryLinkClicked
                            )}
                        </span>
                    </div>
                )}
            </section>
        );
    }

    private renderReleaseSection(): JSX.Element {
        return (
            <section aria-label={PackageResources.Release}>
                <div className="package-group-header" role="heading" aria-level={3}>
                    {PackageResources.Release}
                </div>
                <div className="package-attribute-item">
                    {this.hasLink(this.state.links.releaseDefinitionLink) && (
                        <span>
                            <Icon iconName="Rocket" className="package-attribute-item-icon" />
                            {this.renderLink(
                                this.state.links.releaseDefinitionLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceReleaseDefinition,
                                    this.state.links.releaseDefinitionLink.text
                                ),
                                CiConstants.ProvenanceReleaseDefinitionLinkClicked
                            )}
                        </span>
                    )}
                    {this.hasLink(this.state.links.releaseLink) &&
                        this.hasLink(this.state.links.releaseDefinitionLink) && <span className="separator">/</span>}
                    {this.hasLink(this.state.links.releaseLink) && (
                        <span>
                            {this.renderLink(
                                this.state.links.releaseLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceRelease,
                                    this.state.links.releaseLink.text
                                ),
                                CiConstants.ProvenanceReleaseLinkClicked
                            )}
                        </span>
                    )}
                </div>
                {this.renderPublishDate()}
            </section>
        );
    }

    private renderExternalSection(): JSX.Element {
        return (
            <section aria-label={PackageResources.External}>
                <div className="package-group-header" role="heading" aria-level={3}>
                    {PackageResources.External}
                </div>
                <div className="package-attribute-item">
                    {this.state.links.externalLink != null && (
                        <span>
                            <Icon iconName="NavigateExternalInline" className="package-attribute-item-icon" />
                            {this.renderLink(
                                this.state.links.externalLink,
                                Utils_String.format(
                                    PackageResources.AriaLabel_ProvenanceExternal,
                                    this.state.links.externalLink.text
                                ),
                                CiConstants.ProvenanceExternalLinkClicked
                            )}
                        </span>
                    )}
                </div>
                {this.renderPublishDate()}
            </section>
        );
    }

    private renderLink(link: IProvenanceLinkData, ariaLabel: string, ciFeature: string): JSX.Element {
        if (link == null || link.text == null) {
            return null;
        }

        const decodedLinkText = Utils_Html.htmlDecode(link.text);

        if (link.url == null) {
            return <span aria-label={ariaLabel}>{link.text}</span>;
        }

        if (link.isExternal) {
            try {
                return (
                    <ExternalLink
                        pageContext={this.props.pageContext}
                        href={link.url}
                        linkAriaLabel={ariaLabel}
                        ciFeature={ciFeature}
                    >
                        {decodedLinkText}
                    </ExternalLink>
                );
            } catch (error) {
                if (error instanceof URIError) {
                    return (
                        <div className="provenanceUriFailureText">Resources.PackageProvenance_LinkRenderingFailed</div>
                    );
                } else {
                    throw error;
                }
            }
        }

        return (
            <InternalLink
                pageContext={this.props.pageContext}
                href={link.url}
                linkAriaLabel={ariaLabel}
                ciFeature={ciFeature}
            >
                {decodedLinkText}
            </InternalLink>
        );
    }

    private hasLink(link: IProvenanceLinkData): boolean {
        return link != null && link.text != null;
    }

    private canShowProvenance(): boolean {
        return this.props.isProvenanceEnabled && this.props.isProvenanceSupported && this.state.provenance != null;
    }

    private showBuild(): boolean {
        return (
            this.canShowProvenance() &&
            Utils_String.equals(this.state.provenance.provenanceSource, ProvenanceSource.InternalBuild, true) &&
            this.state.links != null &&
            (this.state.links.buildLink != null || this.state.links.buildDefinitionLink != null)
        );
    }

    private showRelease(): boolean {
        return (
            this.canShowProvenance() &&
            Utils_String.equals(this.state.provenance.provenanceSource, ProvenanceSource.InternalRelease, true) &&
            this.state.links != null &&
            (this.state.links.releaseLink != null || this.state.links.releaseDefinitionLink != null)
        );
    }

    private showExternal(): boolean {
        return (
            this.canShowProvenance() &&
            Utils_String.equals(this.state.provenance.provenanceSource, ProvenanceSource.External, true) &&
            this.state.links != null &&
            this.state.links.externalLink != null
        );
    }

    private showCode(): boolean {
        return (
            this.canShowProvenance() &&
            this.showBuild() &&
            this.state.links != null &&
            (this.state.links.repositoryLink != null ||
                this.state.links.commitLink != null ||
                this.state.links.branchLink != null)
        );
    }

    private getRepoIcon(repoType: string) {
        if (Utils_String.equals(repoType, RepositoryTypes.TfsVersionControl, true)) {
            return "TFVCLogo";
        }
        if (Utils_String.equals(repoType, RepositoryTypes.TfsGit, true)) {
            return "GitLogo";
        }
        if (
            Utils_String.equals(repoType, RepositoryTypes.GitHub, true) ||
            Utils_String.equals(repoType, RepositoryTypes.GitHubEnterprise, true)
        ) {
            return "GitHubLogo";
        }
        if (Utils_String.equals(repoType, RepositoryTypes.ExternalGit, true)) {
            return "ExternalGit";
        }
        if (Utils_String.equals(repoType, RepositoryTypes.Svn, true)) {
            return "SVNLogo";
        }
        if (Utils_String.equals(repoType, RepositoryTypes.Bitbucket, true)) {
            return "BitbucketLogo32";
        }
        // Generic
        return "Repo";
    }
}
